import React, { useCallback, useRef, useState } from 'react';
import { UserRoles } from '@/enums/enums';
import {
  useDeleteFileMutation,
  useDownloadFileMutation,
} from '@/features/file-management/file/file-management-file.mutations';
import { useDeleteFolderMutation } from '@/features/file-management/folder/file-management-folder.mutations';
import {
  useFolderFiles,
  useFolders,
} from '@/features/file-management/folder/file-management-folder.queries';
import { getUserOptions } from '@/features/user/user.queries';
import { formatBytes, saveFileAs } from '@/utils/file-utils';
import { Separator } from '@radix-ui/react-separator';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, getRouteApi, redirect, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import { Download, File, Folder, FolderPlus, Pencil, Slash, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FileUploader } from '@/components/file-uploader';
import AlertModal from '@/components/modals/alert-modal';
import CreateUpdateFolderModal from '@/components/modals/create-update-folder-modal';
import { TreeDataItem, TreeView } from '@/components/tree-view';

const fileManagementRouteSchema = z.object({
  mode: z.enum(['create', 'update']).optional(),
  folder: z.number().optional(),
  parent: z.number().optional(),
});

export const Route = createFileRoute('/_app/(file-management)/file-management/')({
  validateSearch: zodSearchValidator(fileManagementRouteSchema),
  component: RouteComponent,
  beforeLoad: async ({ context }) => {
    const { user } = await context.queryClient.ensureQueryData(getUserOptions);

    if (![UserRoles.Root, UserRoles.Admin, UserRoles.Secretary].includes(user?.role as UserRoles)) {
      throw redirect({
        to: '/',
      });
    }
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const { folder } = getRouteApi('/_app/(file-management)/file-management/').useSearch();

  const { data = [], isLoading } = useFolders();

  const createRootFolder = () => {
    navigate({ to: '.', search: { mode: 'create' } });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className='flex h-full gap-y-2 px-4 py-2'>
      <div className='h-full min-h-0 flex-shrink-0 flex-col rounded-md border border-b-2'>
        <div className='flex items-center justify-between p-2'>
          <span className='font-bold'>Φάκελοι</span>
          <Button size={'icon'} variant={'outline'} onClick={createRootFolder}>
            <FolderPlus className='h-6 w-6' />
          </Button>
        </div>
        <Separator className='h-px w-full bg-black' />

        <TreeView
          className='h-full min-w-64 overflow-y-auto'
          data={data}
          defaultNodeIcon={() => <Folder size={15} className='mr-1' />}
          defaultLeafIcon={() => <File size={15} className='mr-1' />}
        />
      </div>

      {/* main area */}
      <div className='flex flex-1 flex-col gap-y-2 px-4 py-2'>
        {folder ? (
          <>
            <FileManagementHeader folders={data} folderId={folder} />
            <DropzoneFileUploader />
            <FolderFilesTable folder={folder} />
          </>
        ) : (
          <div className='flex h-full items-center justify-center'>
            <span className='text-2xl font-semibold text-red-800'>
              Παρακαλώ επιλέξτε έναν φάκελο για να συνεχίσετε
            </span>
          </div>
        )}
      </div>
      <CreateUpdateFolderModal />
    </div>
  );
}

function FileManagementHeader({
  folders,
  folderId,
}: {
  folders: TreeDataItem[];
  folderId: string;
}) {
  const navigate = useNavigate();
  const { mutateAsync: deleteMutateAsync } = useDeleteFolderMutation();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const selectedId = useRef<number>(0);
  const createChildFolder = () => {
    navigate({ to: '.', search: { folder: folderId, mode: 'create', parent: folderId } });
  };

  const onDeleteFolder = async (id: number) => {
    selectedId.current = id;
    setIsDeleteModalOpen(true);
  };

  const onEditFolder = async (id: number) => {
    navigate({ to: '.', search: { folder: id, mode: 'update' } });
  };

  const onSuccessActive = useCallback(async () => {
    await deleteMutateAsync(selectedId.current);
    selectedId.current = 0;
    navigate({ to: '.' });
  }, [deleteMutateAsync, selectedId, navigate]);

  const path = findPath({ folders, folderId });
  return (
    <>
      <div className='flex flex-row justify-between py-4'>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbSeparator>
              <Slash />
            </BreadcrumbSeparator>

            {path.map((item, idx) => (
              <React.Fragment key={item.id}>
                {idx < path.length - 1 ? (
                  <BreadcrumbItem>
                    <BreadcrumbLink href={`?folder=${item.id}`}>{item.name}</BreadcrumbLink>
                  </BreadcrumbItem>
                ) : (
                  <BreadcrumbItem>
                    <BreadcrumbPage>{item.name}</BreadcrumbPage>
                  </BreadcrumbItem>
                )}
                {idx < path.length - 1 && (
                  <BreadcrumbSeparator>
                    <Slash />
                  </BreadcrumbSeparator>
                )}
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div className='flex flex-row items-center gap-x-2'>
        <span className='text-xl font-bold'>{path[path.length - 1].name}</span>
        {path.length <= 4 && (
          <Button variant={'outline'} onClick={createChildFolder}>
            <FolderPlus className='h-6 w-6' /> Φάκελος
          </Button>
        )}
        <Button variant={'outline'} onClick={() => onEditFolder(parseInt(folderId))}>
          <Pencil />
        </Button>
        <Button variant={'outline'} color='red' onClick={() => onDeleteFolder(parseInt(folderId))}>
          <Trash2 color='red' />
        </Button>
      </div>
      <AlertModal
        title={'Διαγραφή φακέλου'}
        description={'Είστε σίγουροι ότι θέλετε να διαγράψετε αυτόν τον φακελο;'}
        setOpen={setIsDeleteModalOpen}
        isOpen={isDeleteModalOpen}
        onSuccess={onSuccessActive}
      />
    </>
  );
}

function DropzoneFileUploader() {
  const queryClient = useQueryClient();
  const handleChange = () => {
    queryClient.invalidateQueries({ queryKey: ['folders'] });
  };
  return (
    <div className='flex w-full flex-col gap-2'>
      <FileUploader
        onValueChange={handleChange}
        module='files'
        multiple
        showUploadedFiles={false}
        maxFileCount={Infinity}
        maxSize={1024 * 1024 * 5}
        accept={{
          'image/jpeg': [],
          'image/png': [],
          'application/pdf': ['.pdf'],
          'application/vnd.ms-excel': ['.xls', '.slk', '.xla', '.xlt', '.xlw'],
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          'application/msword': ['.doc'],
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
          'application/vnd.oasis.opendocument.spreadsheet': ['.ods'],
          'application/vnd.oasis.opendocument.text': ['.odt'],
          'text/plain': ['.txt'],
          'text/csv': ['.csv'],
        }}
      />
    </div>
  );
}

function FolderFilesTable({ folder }: { folder: number }) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const selectedId = useRef<number>(0);

  const { t } = useTranslation(['table']);

  const { mutateAsync } = useDownloadFileMutation();
  const { mutateAsync: deleteMutateAsync } = useDeleteFileMutation();
  const { data = [], isLoading, error } = useFolderFiles(folder);

  const onDownloadFile = async (id: number, type: string, name: string) => {
    const response = await mutateAsync(id);
    saveFileAs({ data: response, type, filename: name });
  };

  const onDeleteFile = async (id: number) => {
    selectedId.current = id;
    setIsDeleteModalOpen(true);
  };

  const onSuccessActive = useCallback(async () => {
    await deleteMutateAsync(selectedId.current);
    selectedId.current = 0;
  }, [deleteMutateAsync, selectedId]);

  if (isLoading) return <div>Φορτώνει αρχεία…</div>;
  if (error) return <div>Σφάλμα κατά τη φόρτωση των αρχείων</div>;

  return (
    <div className='relative h-full w-full overflow-auto'>
      <Table className='border-collapse'>
        <TableHeader>
          <TableRow className='items-center justify-center'>
            <TableHead>{t('serial_id')}</TableHead>
            <TableHead>{t('name')}</TableHead>
            <TableHead>{t('size')}</TableHead>
            <TableHead>{t('type')}</TableHead>
            <TableHead>{t('uploaded_at')}</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((file) => (
            <TableRow key={file.id}>
              <TableCell>{file.id}</TableCell>
              <TableCell>{file.originalFilename}</TableCell>
              <TableCell>{formatBytes(file.size)}</TableCell>
              <TableCell>{file.type}</TableCell>
              <TableCell>
                {format(file.uploadedAt, 'EEEE, d MMMM HH:mm', {
                  locale: el,
                })}
              </TableCell>
              <TableCell className='flex gap-x-2'>
                <Button
                  variant={'outline'}
                  onClick={() => onDownloadFile(file.id, file.type, file.originalFilename)}
                >
                  <Download />
                </Button>
                <Button variant={'outline'} color='red' onClick={() => onDeleteFile(file.id)}>
                  <Trash2 color='red' />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <AlertModal
        title={'Διαγραφή αρχείου'}
        description={'Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το αρχείο;'}
        setOpen={setIsDeleteModalOpen}
        isOpen={isDeleteModalOpen}
        onSuccess={onSuccessActive}
      />
    </div>
  );
}

function findPath({ folders, folderId }: { folders: TreeDataItem[]; folderId: string }) {
  const result: TreeDataItem[] = [];

  function dfs(nodes: TreeDataItem[], targetId: string): boolean {
    for (const node of nodes) {
      result.push(node);
      if (node.id === targetId && node.children) {
        return true;
      }
      if (node.children && dfs(node.children, targetId)) {
        return true;
      }
      result.pop();
    }
    return false;
  }

  dfs(folders, folderId);
  return result;
}
